import { ComponentType } from '@angular/cdk/portal';
import { Inject, Injectable, Optional } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import {
  FlamingoAlertDialogComponent,
  FlamingoAlertDialogModel,
} from '@flamingo/component/flamingo-alert-dialog.component';
import { Observable } from 'rxjs';
import { FLAMINGO_APP_CONFIG, FlamingoAppConfig } from '../flamingo.config';
import {
  FlamingoConfirmDialogComponent,
  FlamingoConfirmDialogOptions,
} from '../component/flamingo-confirm-dialog.component';
import { FlamingoInputDialogComponent, FlamingoInputDialogOptions } from '../component/flamingo-input-dialog.component';
import { FlamingoSnackBarLoaderComponent } from '../component/snackbar-loader.component';

const DEFAULT_MESSAGE_DURATION = 5000;

@Injectable()
export class FlamingoMessageService {
  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Optional() @Inject(FLAMINGO_APP_CONFIG) private config?: FlamingoAppConfig,
  ) {}

  showMessage(message: string, duration = this.config?.messageDuration ?? DEFAULT_MESSAGE_DURATION): void {
    this.snackBar.open(message, undefined, { duration });
  }

  dismissMessage(): void {
    this.snackBar.dismiss();
  }

  showMessageWithConfirm(message: string, action = 'OK'): void {
    this.snackBar.open(message, action);
  }

  showAlert(data: FlamingoAlertDialogModel, config?: MatDialogConfig): Observable<void> {
    return this.dialog.open(FlamingoAlertDialogComponent, { ...config, data }).afterClosed();
  }

  showMessageWithLoader(message: string): void {
    const config = new MatSnackBarConfig();
    config.data = {
      message,
    };
    this.snackBar.openFromComponent(FlamingoSnackBarLoaderComponent, config);
  }

  // R is the dialog result type: callers declare it, MatDialog cannot infer it from the component
  showModal<R = unknown>(
    component: ComponentType<unknown>,
    data: unknown = {},
    autoFocus?: boolean,
    disableClose?: boolean,
    width?: number,
  ): Observable<R | undefined> {
    return this.dialog
      .open<unknown, unknown, R>(component, {
        width: (width ? width : '600') + 'px',
        maxHeight: '95vh',
        maxWidth: '90vw',
        autoFocus,
        disableClose,
        data,
      })
      .afterClosed();
  }

  showConfirmDialog(options: FlamingoConfirmDialogOptions, config?: MatDialogConfig): Observable<boolean> {
    return this.dialog
      .open(FlamingoConfirmDialogComponent, {
        ...config,
        data: options,
      })
      .afterClosed();
  }

  showInputDialog(options: FlamingoInputDialogOptions, config?: MatDialogConfig): Observable<string> {
    return this.dialog
      .open(FlamingoInputDialogComponent, {
        ...config,
        data: options,
      })
      .afterClosed();
  }
}
